import { defineStore } from "pinia";
import { ref } from "vue";
import { useCloudClientStore } from "@/stores/cloud-client.js";
import { UAParser } from "ua-parser-js";
import { Crisp } from "crisp-sdk-web";

export const useCompatibilityCheckStore = defineStore(
  "compatibilityCheck",
  () => {
    const hardwareSpecifications = ref({});

    const cloudClient = useCloudClientStore();

    const currentStatus = ref({
      name: "inProgress",
      icon: "bi-arrow-repeat",
      title: "Votre test est en cours de chargement",
      content:
        "Veuillez patienter, nous vérifions un à un les paramètres ci-dessous pour évaluer la compatibilité et la performance de votre configuration.",
    });

    const status = ref([
      {
        name: "error",
        icon: "bi-x-circle",
        title: "Résultats insuffisants",
        content:
          "Les résultats montrent que certains critères de connexion ou de compatibilité ne sont pas satisfaits. Cliquez sur « En savoir plus » pour obtenir des conseils d’amélioration pour chaque élément. Notre support est également disponible en cas de besoin, accessible en bas à droite de votre écran.",
        buttons: [
          {
            label: "Recommencer le test",
            class: "btn primary",
            action: () => {
              resetCheck();
              launchTests();
            },
          },
          {
            label: "Contactez nous en ligne",
            class: "btn outline light icon",
            icon: "bi-chat-left-dots",
            action: () => {
              Crisp.chat.open();
            },
          },
        ],
      },
      {
        name: "bad",
        icon: "bi-x-circle",
        title: "Résultats défavorables",
        content:
          "Les résultats montrent que certains critères de connexion ou de compatibilité ne sont pas satisfaits. Cliquez sur « En savoir plus » pour obtenir des conseils d’amélioration pour chaque élément. Notre support est également disponible en cas de besoin, accessible en bas à droite de votre écran.",
        buttons: [
          {
            label: "Recommencer le test",
            class: "btn primary",
            action: () => {
              resetCheck();
              launchTests();
            },
          },
          {
            label: "Ignorer et continuer vers l’évaluation",
            class: "btn outline light icon",
            action: (state) => {
              state.showCompatibilityWarning = true;
            },
          },
        ],
      },
      {
        name: "warn",
        icon: "bi-exclamation-circle",
        title: "Résultats passables",
        content:
          "Vos résultats indiquent que les conditions sont suffisantes pour lancer votre évaluation. Pour optimiser votre expérience, consultez les conseils d’amélioration en cliquant sur « En savoir plus » pour chaque élément. Notre support est également à votre disposition en bas à droite de l'écran si besoin.",
        buttons: [
          {
            label: "Recommencer le test",
            class: "btn primary",
            action: () => {
              resetCheck();
              launchTests();
            },
          },
          {
            label: "Ignorer et continuer vers l’évaluation",
            class: "btn outline light icon",
            action: (state) => {
              state.showCompatibilityWarning = true;
            },
          },
        ],
      },
      {
        name: "success",
        icon: "bi-check-circle",
        title: "Résultats satisfaisants",
        content:
          "Votre environnement semble adapté pour effectuer votre aventure Yuzu dans de bonnes conditions.",
        buttons: [
          {
            label: "Continuer et effectuer les vérifications préliminaires",
            class: "btn primary",
            action: (state) => {
              state.finish = true;
              cloudClient.pushHardwareSpecs(hardwareSpecifications.value);
            },
          },
        ],
      },
    ]);

    const tests = ref([
      {
        icon: "bi-wifi",
        title: "Connexion internet",
        description:
          "Délai de communication entre votre ordinateur et notre serveur de jeu.",
        check: async () => {
          let data = null;

          const promises = [];

          const promise = async () => {
            const networkData = await speedTest();

            if (networkData) {
              const quality = await getSessionQuality(
                networkData.networkPing.value,
                networkData.networkBandwidth.value
              );
              if (quality === "FAILED") {
                data = {
                  name: "error",
                  message: "Connexion impossible",
                };
              } else {
                data = {
                  name: "success",
                  message: "Connexion stable",
                };
              }

              data = {
                ...data,
                data: { ...networkData },
              };

              hardwareSpecifications.value = {
                ...hardwareSpecifications.value,
                ...networkData,
              };
            }
          };

          promises.push(promise);

          await Promise.all(promises.map((asyncFn) => asyncFn()));

          return data;
        },
        result: null,
        informations:
          "Votre ordinateur se connecte via internet à notre serveur de jeu. Si la communication entre ces deux parties est trop lente, cela risque d’affecter négativement votre expérience de jeu sur Yuzu.",
        informationsBad: "Votre connexion actuelle à notre serveur est lente, ce qui peut nuire à la qualité de votre expérience de jeu sur Yuzu.",
          advices: [
          {
            icon: "bi-display",
            label:
              "Utilisez un câble Ethernet : Branchez votre ordinateur directement pour éviter les limitations du WiFi.",
          },
          {
            icon: "bi-pause-fill",
            label:
              "Limitez les autres activités en ligne : Fermez les applications ou téléchargements qui consomment de la bande passante (vidéos, streaming, etc.).",
          },
          {
            icon: "bi-power",
            label: "Redémarrez votre box internet : Cela peut souvent améliorer la connexion.",
          },
          {
            icon: "bi-wifi-off",
            label: "Déconnectez les appareils non nécessaires en WiFi : Plus il y a d'appareils connectés, plus la bande passante est partagée.",
          },
          {
            icon: "bi-reception-4",
            label: "Essayez une connexion plus puissante : Si possible, testez Yuzu avec une connexion plus rapide pour des performances optimales.",
          },
        ],
      },
      {
        icon: "bi-globe",
        title: "Navigateur",
        description:
          "Navigateur internet utilisé pour effectuer votre session d’évaluation.",
        check: async () => {
          let data = null;

          const promise = new Promise((resolve) => {
            const browserData = browserTest();

            const compatibleBrowser = ["Brave", "Chromium", "Firefox", "Chrome"];
            const checkBrowser = ["Edge"];
            const checkBrowserVersions = {"Edge": [79, 0, 309]};

            setTimeout(() => {
              if (browserData) {
                if (compatibleBrowser.includes(browserData.browserAgent.value)) {
                  data = {
                    name: "success",
                    message: "Navigateur compatible",
                  };
                } else if (checkBrowser.includes(browserData.browserAgent.value)) {
                  // check if version is ok
                  const minVersion = checkBrowserVersions[browserData.browserAgent.value];                  
                  const [major, minor, patch] = browserData.browserVersion.value.split('.').map(Number);

                  var validVersion = false;
                  if (major > minVersion[0])
                  {
                    validVersion = true;
                  }
                  else if (major === minVersion[0] && minor > minVersion[1])
                  {
                    validVersion = true;
                  }
                  else if (major === minVersion[0] && minor === minVersion[1] && patch >= minVersion[3])
                  {
                    validVersion = true;
                  }

                  if(validVersion) {
                    data = {
                      name: "success",
                      message: "Navigateur compatible",
                    };
                  }
                  else {
                    data = {
                      name: "error",
                      message: "Navigateur incompatible",
                    };
                  }
                } else {
                  data = {
                    name: "error",
                    message: "Navigateur incompatible",
                  };
                }

                data = {
                  ...data,
                  data: { ...browserData },
                };

                hardwareSpecifications.value = {
                  ...hardwareSpecifications.value,
                  ...browserData,
                };
              }
              resolve();
            }, 1500);
          });

          return promise.then(() => {
            return data;
          });
        },
        result: null,
        informations:
          "Votre navigateur est l’outil utilisé pour accéder à internet. Les navigateurs courants incluent Chrome, Edge et Firefox. Utiliser une version trop ancienne ou un navigateur non compatible peut affecter négativement votre expérience de jeu sur Yuzu.",
        advices: [
          {
            icon: "bi-arrow-up",
            label: "Mettez à jour votre navigateur : Assurez-vous d’utiliser la dernière version pour une meilleure compatibilité.",
          },
          {
            icon: "bi-google",
            label:
              "Téléchargez un navigateur compatible avec Yuzu : Nous recommandons Chrome, Firefox ou Edge.",
          },
          {
            icon: "bi-arrow-left-right",
            label: "Essayez depuis un autre appareil : Si possible, testez Yuzu sur un autre ordinateur pour vérifier la compatibilité.",
          },
        ],
      },
      {
        icon: "bi-graph-up-arrow",
        title: "Performances",
        description: "Performances matérielles de votre ordinateur.",
        check: async () => {
          let data = null;

          const promise = new Promise((resolve) => {
            (async () => {
              const performanceData = await perfomancesTest();

              setTimeout(() => {
                data = {
                  name: "success",
                  message: "Performances suffisantes",
                  data: { ...performanceData },
                };

                hardwareSpecifications.value = {
                  ...hardwareSpecifications.value,
                  ...performanceData,
                };

                resolve();
              }, 5000);
            })();
          });

          return promise.then(() => {
            return data;
          });
        },
        result: null,
        informations:
          "Les performances matérielles d’un ordinateur font référence aux éléments qui le composent : RAM, Processeur, espace disque… Utiliser un ordinateur trop peu puissant risque d’affecter négativement votre expérience de jeu sur Yuzu.",
        advices: [
          {
            icon: "bi-arrow-left-right",
            label: "Essayez depuis un autre ordinateur.",
          },
        ],
      },
      {
        icon: "bi-display",
        title: "Taille de la fenêtre",
        description: "Taille de cette fenêtre internet.",
        check: async () => {
          let data = null;

          const promise = new Promise((resolve) => {
            const screenData = screenTest();

            setTimeout(() => {
              if (screenData) {
                if (
                  screenData.screenWidth.value < 900 ||
                  screenData.screenHeight.value < 500
                ) {
                  data = {
                    name: "bad",
                    message: "Fenêtre trop petite",
                  };
                } else if (
                  screenData.screenRatio.value < 1 ||
                  screenData.screenRatio.value > 2.5 ||
                  screenData.screenHeight.value < 800
                ) {
                  data = {
                    name: "warn",
                    message: "Fenêtre acceptable",
                  };
                } else {
                  data = {
                    name: "success",
                    message: "Fenêtre adaptée",
                  };
                }

                data = {
                  ...data,
                  data: { ...screenData },
                };

                hardwareSpecifications.value = {
                  ...hardwareSpecifications.value,
                  ...screenData,
                };
              }
              resolve();
            }, 1000);
          });

          return promise.then(() => {
            return data;
          });
        },
        result: null,
        informations:
          "Pour profiter de Yuzu dans les meilleures conditions, nous vous recommandons d'agrandir votre fenêtre au maximum afin qu'elle utilise toute la surface de votre écran.",
        advices: [
          {
            icon: "bi-arrows-angle-expand",
            label: "Agrandissez la fenêtre : Maximisez la taille de la fenêtre pour une meilleure visibilité.",
          },
          {
            icon: "bi-zoom-out",
            label: "Réinitialisez le zoom du navigateur : Assurez-vous que le zoom est à 100 %.",
          },
          {
            icon: "bi-display",
            label: "Utilisez un écran plus grand : Branchez un écran de taille supérieure si possible.",
          },
          {
            icon: "bi-arrow-left-right",
            label: "Essayez sur un autre appareil : Testez Yuzu sur un ordinateur avec une résolution d'écran plus élevée.",
          },
        ],
      },
      {
        icon: "bi-hdd",
        title: "Instance",
        description: "Disponibilité de nos serveurs de jeu.",
        check: async () => {
          let data = null;

          const promise = new Promise((resolve) => {
            cloudClient.connectToCloudOperator().then((success) => {
              if (!success) {
                const instanceDisponibility = {
                  value: "Non disponible",
                  label: "Disponibilité",
                };

                data = {
                  name: "error",
                  message: "Contexte invalide",
                  data: {
                    instanceDisponibility,
                  },
                };

                hardwareSpecifications.value = {
                  ...hardwareSpecifications.value,
                  instanceDisponibility,
                };

                resolve();
                return;
              }

              cloudClient.emitter.off("onSessionAvailable");
              cloudClient.emitter.on("onSessionAvailable", () => {
                const instanceDisponibility = {
                  value: "Disponible",
                  label: "Disponibilité",
                };

                data = {
                  name: "success",
                  message: "Cloud disponible",
                  data: {
                    instanceDisponibility,
                  },
                };

                hardwareSpecifications.value = {
                  ...hardwareSpecifications.value,
                  instanceDisponibility,
                };

                resolve();
              });

              cloudClient.emitter.off("onSessionNotAvailable");
              cloudClient.emitter.on("onSessionNotAvailable", (event) => {
                const instanceDisponibility = {
                  value: "Disponible",
                  label: "Non disponible",
                };

                var message = "";
                if(event.maintenance) {
                  message = "Cloud en maintenance";
                }
                else if (event.error) {
                  message = "Erreur momentanée";
                }
                else if (event.started) {
                  message =  "Session en cours";
                }
                else {
                  message = "Cloud indisponible";
                }
                
                data = {
                  name: "error",
                  message: message,
                  data: {
                    instanceDisponibility,
                  },
                };

                hardwareSpecifications.value = {
                  ...hardwareSpecifications.value,
                  instanceDisponibility,
                };

                resolve();
              });

              cloudClient.emitter.off("onCloudOperatorSocketDisconnected"); 
              cloudClient.emitter.on(
                "onCloudOperatorSocketDisconnected",
                () => {
                  const instanceDisponibility = {
                    value: "Non disponible",
                    label: "Disponibilité",
                  };

                  data = {
                    name: "error",
                    message: "Connexion échouée",
                    data: {
                      instanceDisponibility,
                    },
                  };

                  hardwareSpecifications.value = {
                    ...hardwareSpecifications.value,
                    instanceDisponibility,
                  };

                  resolve();
                }
              );
            });
          });

          return promise.then(() => {
            return data;
          });
        },
        result: null,
        informations:
          "Pour jouer directement depuis votre navigateur sans installer Yuzu, un serveur de jeu dédié vous est attribué. Nous vérifions actuellement sa disponibilité. Si vous avez déjà lancé Yuzu dans un autre onglet ou navigateur, veuillez le fermer avant de recommencer ici.",
        advices: [
          {
            icon: "bi-headset",
            label:
              "Contactez notre support : En cas de problème, n'hésitez pas à contacter notre support via la bulle de chat en bas à droite de l'écran.",
          },
        ],
      },
    ]);

    async function speedTest() {
      try {
        // Get the average ping
        const pingResult = await pingTest();
        console.log("Ping result:", pingResult.networkPing.value);
        // Get the bandwidth
        const bandwidthResult = await bandwidthTest();
        console.log("Bandwidth result:", bandwidthResult.networkBandwidth.value);
        // Send results to operator then wait for response
        return {
            ...pingResult,
            ...bandwidthResult
        };
      } 
      catch (error) {
        return {
          networkPing: {
            value: -1,
            label: "Ping/ms",
          },
          networkBandwidth: {
            value: -1,
            label: "Bandwidth/Mbps",
          },
        };
      }
    }

    async function pingTest() {
      let latency = -1;

      try {
        const promise = new Promise((resolve, reject) => {

          // Set a timeout to handle cases where events do not trigger as expected
          let timeout = null;
          cloudClient.emitter.off("onCancelSpeedtest");
          cloudClient.emitter.on("onCancelSpeedtest", () => {
            reject(new Error("Cancelling Speedtest as there is no capacity"));
          });
          cloudClient.emitter.off("onSpeedTestURLAvailable");
          cloudClient.emitter.on("onSpeedTestURLAvailable", () => {
            try {
              timeout = setTimeout(() => {
                reject(new Error("Ping test timed out"));
              }, 15000); // Timeout after 15 seconds
              cloudClient.emitter.off("onCloudOperatorLatencyUpdated");
              cloudClient.emitter.on(
                "onCloudOperatorLatencyUpdated",
                (new_latency) => {
                  latency = new_latency;
                  clearTimeout(timeout); // Clear timeout on latency recieved
                  resolve(latency);
                }
              );
              cloudClient.startPingTest();
            } catch (error) {
              clearTimeout(timeout);
              reject(new Error("Error starting ping test or setting up latency listener"));
            }
          });
        });

        latency = await promise;

        return {
          networkPing: {
            value: Math.round(latency),
            label: "Ping/ms",
          },
        };
      } catch (error) {
        console.error("Error in pingTest:", error);
        return {
          networkPing: {
            value: -1,
            label: "Ping/ms"
          },
        };
      }
    }

    async function bandwidthTest() {
      let speedInMbps = -1;
      try {
        const promise = new Promise((resolve) => {
          cloudClient.emitter.off("onCloudOperatorBandwidthUpdated");
          cloudClient.emitter.on(
            "onCloudOperatorBandwidthUpdated",
            (new_speedInMbps) => {
              speedInMbps = new_speedInMbps;
              resolve();
            }
          );
          cloudClient.startDownloadTest();
        });

        return promise.then(() => {
          return {
            networkBandwidth: {
              value: Math.round(speedInMbps),
              label: "Bandwidth/Mbps",
            },
          };
        });
      } catch (error) {
        console.error("Error in bandwidthTest:", error);
        return {
          networkBandwidth: {
            value: -1,
            label: "Bandwidth/Mbps"
          },
        };
      }
    }

    function getSessionQuality(ping, bandwidth) {
      try {
        const promise = new Promise((resolve, reject) => {
          const timeout = setTimeout(() => {
            reject(new Error("Could not retrieve session quality"));
          }, 10000); // Timeout after 10 seconds
          cloudClient.sendSpeedTestResults(ping, bandwidth);
          cloudClient.emitter.on("onCloudOperatorSessionQuality", (quality) => {
            clearTimeout(timeout);
            resolve(quality);
          });
        });

        return promise.then((quality) => {
          return quality;
        });
      } catch (error) {
        console.error("Could not retrieve session quality:", error);
        return "FAILED";
      }
    }

    function browserTest() {
      if (navigator) {
        const { browser } = UAParser(navigator.userAgent);

        return {
          browserAgent: {
            value: browser.name,
            label: "Application logicielle",
          },
          browserVersion: {
            value: browser.version,
            label: "Version",
          },
        };
      }

      return null;
    }

    async function perfomancesTest() {
      if (navigator) {
        let osObj;

        const parser = new UAParser(navigator.userAgent);

        if (import.meta.env.PROD) {
          const osParser = await parser.getOS().withClientHints();

          osObj = {
            platform: osParser.name,
            platformVersion: osParser.version,
          };
        } else {
          osObj = {
            platform: parser.getOS().name,
            platformVersion: parser.getOS().version,
          };
        }

        return {
          hardwareRam: {
            value:
              navigator.deviceMemory == 8
                ? `Min. ${navigator.deviceMemory}`
                : navigator.deviceMemory,
            label: "RAM",
          },
          hardwareCpuCores: {
            value: navigator.hardwareConcurrency,
            label: "CPU cores",
          },
          hardwareOs: {
            value: `${osObj.platform} ${osObj.platformVersion}`,
            label: "OS",
          },
        };
      }

      return null;
    }

    function screenTest() {
      if (window) {
        return {
          screenWidth: {
            value: `${window.innerWidth}`,
            label: "Largeur d'écran (px)",
          },
          screenHeight: {
            value: `${window.innerHeight}`,
            label: "Hauteur d'écran (px)",
          },
          screenRatio: {
            value: parseFloat(
              (window.innerWidth / window.innerHeight).toFixed(2)
            ),
            label: "Ratio",
          },
        };
      }

      return null;
    }

    async function launchTests() {
      //disconnect to make session available again for current session ID
      await cloudClient.disconnectFromCloudOperator();

      const promises = [];

      tests.value.forEach((test) => {
        const promise = async () => {
          test.result = await test.check();
        };

        promises.push(promise);
      });

      await Promise.all(promises.map((checkFn) => checkFn()));

      setStatus();
    }

    function setStatus() {
      const results = tests.value.map((test) => test.result);

      const warnings = results.filter((result) => result?.name === "warn");

      const bad = results.filter((result) => result?.name === "bad");

      const errors = results.filter((result) => result?.name === "error");

      if (errors.length > 0) {
        currentStatus.value = status.value[0];
      } 
      else if (bad.length > 0) {
        currentStatus.value = status.value[1];
      } 
      else if (warnings.length > 0) {
        currentStatus.value = status.value[2];
      } 
      else {
        currentStatus.value = status.value[3];
      }
    }

    function resetCheck() {
      currentStatus.value = {
        name: "inProgress",
        icon: "bi-arrow-repeat",
        title: "Votre test est en cours de chargement",
        content:
          "Veuillez patienter, nous vérifions un à un les paramètres ci-dessous pour évaluer la compatibilité et la performance de votre configuration.",
      };

      tests.value.forEach((test) => {
        test.result = null;
      });
    }

    return {
      hardwareSpecifications,
      currentStatus,
      tests,
      launchTests,
      resetCheck,
    };
  }
);
